<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import addDays from 'date-fns/addDays';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import endOfDay from 'date-fns/endOfDay';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import { getQuantileIntervals } from '@chatwoot/utils';
import reportsAPI from 'dashboard/api/reports';
import {
  downloadCsvFile,
  generateFileName,
} from 'dashboard/helper/downloadHelper';
import BarChart from 'shared/components/charts/BarChart.vue';
import { DATE_RANGE_OPTIONS } from './constants';

const QUEUED_METRIC_KEY = 'conversations_started_by_customer_count';
const LEFT_METRIC_KEY = 'missed_chats_count';
const HEATMAP_QUANTILES = [0.2, 0.4, 0.6, 0.8, 0.9, 0.99];

const SUPPORTED_RANGE_IDS = [
  DATE_RANGE_OPTIONS.TODAY.id,
  DATE_RANGE_OPTIONS.YESTERDAY.id,
  DATE_RANGE_OPTIONS.LAST_7_DAYS.id,
  DATE_RANGE_OPTIONS.LAST_30_DAYS.id,
  DATE_RANGE_OPTIONS.LAST_3_MONTHS.id,
];

const DATE_RANGE_BY_ID = Object.values(DATE_RANGE_OPTIONS).reduce(
  (acc, option) => {
    acc[option.id] = option;
    return acc;
  },
  {}
);

const { t } = useI18n();

const selectedDateRangeId = ref(DATE_RANGE_OPTIONS.LAST_7_DAYS.id);
const isHourlyDistributionEnabled = ref(false);
const isLoading = ref(false);
const errorMessage = ref('');
const generatedAt = ref(null);
const isConversionBreakdownOpen = ref(true);
const isWaitingBreakdownOpen = ref(true);
const heatmapWrapRef = ref(null);
const hoveredHeatmapCell = ref(null);

const queuedCurrentSeries = ref([]);
const queuedPreviousSeries = ref([]);
const leftCurrentSeries = ref([]);
const leftPreviousSeries = ref([]);
const summaryPayload = ref({});
const waitingSummary = ref({});
const waitingSeries = ref([]);
const heatmapSeries = ref([]);

const [showDateRangeMenu, toggleDateRangeMenu] = useToggle(false);

const selectedDateRange = computed(
  () =>
    DATE_RANGE_BY_ID[selectedDateRangeId.value] ||
    DATE_RANGE_OPTIONS.LAST_7_DAYS
);

const selectedDateRangeLabel = computed(() =>
  t(selectedDateRange.value.translationKey)
);

const dateRangeMenuItems = computed(() =>
  SUPPORTED_RANGE_IDS.map(id => {
    const option = DATE_RANGE_BY_ID[id];
    return {
      action: 'select-date-range',
      value: id,
      label: t(option.translationKey),
      isSelected: id === selectedDateRangeId.value,
    };
  })
);

const getCurrentPeriod = () => {
  const option = selectedDateRange.value;
  const startOffset = option.startOffset ?? option.offset ?? 0;
  const endOffset = option.endOffset ?? option.offset ?? 0;

  const toDate = endOfDay(subDays(new Date(), endOffset));
  const fromDate = startOfDay(subDays(new Date(), startOffset));
  const days = differenceInCalendarDays(toDate, fromDate) + 1;

  return {
    from: getUnixTime(fromDate),
    to: getUnixTime(toDate),
    fromDate,
    toDate,
    days,
  };
};

const currentPeriod = computed(() => getCurrentPeriod());

const previousPeriod = computed(() => {
  const previousToDate = endOfDay(subDays(currentPeriod.value.fromDate, 1));
  const previousFromDate = startOfDay(
    subDays(previousToDate, currentPeriod.value.days - 1)
  );

  return {
    from: getUnixTime(previousFromDate),
    to: getUnixTime(previousToDate),
    fromDate: previousFromDate,
    toDate: previousToDate,
    days: currentPeriod.value.days,
  };
});

const reportGroupBy = computed(() =>
  isHourlyDistributionEnabled.value ? 'hour' : 'day'
);

const normalizeSeries = series => {
  if (!Array.isArray(series)) return [];
  return [...series].sort((left, right) => left.timestamp - right.timestamp);
};

const formatPeriodLabel = period => {
  return `${format(fromUnixTime(period.from), 'dd MMM yy')} - ${format(
    fromUnixTime(period.to),
    'dd MMM yy'
  )}`;
};

const currentPeriodLabel = computed(() =>
  formatPeriodLabel(currentPeriod.value)
);
const previousPeriodLabel = computed(() =>
  formatPeriodLabel(previousPeriod.value)
);

const toNumber = value => Number(value || 0);
const formatDiff = value =>
  `${value > 0 ? '+' : ''}${toNumber(value).toLocaleString()}`;
const formatPercent = value => `${Number(value || 0).toFixed(1)}%`;

const currentQueuedTotal = computed(() =>
  toNumber(summaryPayload.value?.[QUEUED_METRIC_KEY])
);
const previousQueuedTotal = computed(() =>
  toNumber(summaryPayload.value?.previous?.[QUEUED_METRIC_KEY])
);
const currentLeftTotal = computed(() =>
  toNumber(summaryPayload.value?.[LEFT_METRIC_KEY])
);
const previousLeftTotal = computed(() =>
  toNumber(summaryPayload.value?.previous?.[LEFT_METRIC_KEY])
);

const currentEnteredTotal = computed(() =>
  Math.max(currentQueuedTotal.value - currentLeftTotal.value, 0)
);
const previousEnteredTotal = computed(() =>
  Math.max(previousQueuedTotal.value - previousLeftTotal.value, 0)
);

const rateOf = (value, total) => (total > 0 ? (value / total) * 100 : 0);
const currentEnteredRate = computed(() =>
  rateOf(currentEnteredTotal.value, currentQueuedTotal.value)
);
const previousEnteredRate = computed(() =>
  rateOf(previousEnteredTotal.value, previousQueuedTotal.value)
);
const currentLeftRate = computed(() =>
  rateOf(currentLeftTotal.value, currentQueuedTotal.value)
);
const previousLeftRate = computed(() =>
  rateOf(previousLeftTotal.value, previousQueuedTotal.value)
);

const labelByTimestamp = timestamp => {
  const date = fromUnixTime(timestamp);
  return isHourlyDistributionEnabled.value
    ? format(date, 'h a')
    : format(date, 'dd MMM');
};

const tooltipLabelByTimestamp = timestamp => {
  const date = fromUnixTime(timestamp);
  return isHourlyDistributionEnabled.value
    ? format(date, 'dd MMM yy h a')
    : format(date, 'dd MMM yy');
};

const diffClass = diff =>
  diff > 0 ? 'text-n-ruby-9' : diff < 0 ? 'text-n-teal-10' : 'text-n-slate-11';

const conversionRows = computed(() => {
  return queuedCurrentSeries.value.map((queuedCurrent, index) => {
    const queuedPrevious = queuedPreviousSeries.value[index] || {
      value: 0,
      timestamp: queuedCurrent.timestamp,
    };
    const leftCurrent = leftCurrentSeries.value[index] || {
      value: 0,
      timestamp: queuedCurrent.timestamp,
    };
    const leftPrevious = leftPreviousSeries.value[index] || {
      value: 0,
      timestamp: queuedPrevious.timestamp,
    };

    const currentQueued = toNumber(queuedCurrent.value);
    const previousQueued = toNumber(queuedPrevious.value);
    const currentLeft = toNumber(leftCurrent.value);
    const previousLeft = toNumber(leftPrevious.value);
    const currentEntered = Math.max(currentQueued - currentLeft, 0);
    const previousEntered = Math.max(previousQueued - previousLeft, 0);

    return {
      currentTimestamp: queuedCurrent.timestamp,
      previousTimestamp: queuedPrevious.timestamp,
      currentQueued,
      previousQueued,
      queuedDiff: currentQueued - previousQueued,
      currentEntered,
      previousEntered,
      enteredDiff: currentEntered - previousEntered,
      currentLeft,
      previousLeft,
      leftDiff: currentLeft - previousLeft,
      queuedDiffClass: diffClass(currentQueued - previousQueued),
      enteredDiffClass: diffClass(currentEntered - previousEntered),
      leftDiffClass: diffClass(currentLeft - previousLeft),
      currentDateLabel: tooltipLabelByTimestamp(queuedCurrent.timestamp),
      previousDateLabel: tooltipLabelByTimestamp(queuedPrevious.timestamp),
    };
  });
});

const conversionChartCollection = computed(() => ({
  labels: conversionRows.value.map(row =>
    labelByTimestamp(row.currentTimestamp)
  ),
  datasets: [
    {
      label: `${t('QUEUED_CUSTOMERS_REPORT.QUEUED_CUSTOMERS')} (${currentPeriodLabel.value})`,
      data: conversionRows.value.map(row => row.currentQueued),
      backgroundColor: '#1D75E5',
      borderRadius: 4,
      categoryPercentage: 0.8,
      barPercentage: 0.9,
    },
    {
      label: `${t('QUEUED_CUSTOMERS_REPORT.QUEUED_CUSTOMERS')} (${previousPeriodLabel.value})`,
      data: conversionRows.value.map(row => row.previousQueued),
      backgroundColor: '#E7C51B',
      borderRadius: 4,
      categoryPercentage: 0.8,
      barPercentage: 0.9,
    },
    {
      label: `${t('QUEUED_CUSTOMERS_REPORT.ENTERED_CHAT')} (${currentPeriodLabel.value})`,
      data: conversionRows.value.map(row => row.currentEntered),
      backgroundColor: '#C23881',
      borderRadius: 4,
      categoryPercentage: 0.8,
      barPercentage: 0.9,
    },
    {
      label: `${t('QUEUED_CUSTOMERS_REPORT.ENTERED_CHAT')} (${previousPeriodLabel.value})`,
      data: conversionRows.value.map(row => row.previousEntered),
      backgroundColor: '#F97316',
      borderRadius: 4,
      categoryPercentage: 0.8,
      barPercentage: 0.9,
    },
    {
      label: `${t('QUEUED_CUSTOMERS_REPORT.LEFT_QUEUE')} (${currentPeriodLabel.value})`,
      data: conversionRows.value.map(row => row.currentLeft),
      backgroundColor: '#6B7280',
      borderRadius: 4,
      categoryPercentage: 0.8,
      barPercentage: 0.9,
    },
    {
      label: `${t('QUEUED_CUSTOMERS_REPORT.LEFT_QUEUE')} (${previousPeriodLabel.value})`,
      data: conversionRows.value.map(row => row.previousLeft),
      backgroundColor: '#7C3AED',
      borderRadius: 4,
      categoryPercentage: 0.8,
      barPercentage: 0.9,
    },
  ],
}));

const defaultChartOptions = {
  plugins: {
    legend: {
      display: false,
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        callback: value => Number(value).toLocaleString(),
      },
    },
  },
};

const formatDuration = totalSeconds => {
  const seconds = Math.max(Math.round(Number(totalSeconds || 0)), 0);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}min`;
  }
  return `${minutes}min ${remainingSeconds}s`;
};

const formatDurationDiff = value => {
  const numeric = Number(value || 0);
  const sign = numeric > 0 ? '+' : '';
  return `${sign}${formatDuration(Math.abs(numeric))}`;
};

const waitingRows = computed(() => {
  return (waitingSeries.value || []).map(row => {
    const currentEnter = Number(row.current_time_to_enter_seconds || 0);
    const previousEnter = Number(row.previous_time_to_enter_seconds || 0);
    const currentLeave = Number(row.current_time_to_leave_seconds || 0);
    const previousLeave = Number(row.previous_time_to_leave_seconds || 0);

    return {
      ...row,
      currentEnter,
      previousEnter,
      currentLeave,
      previousLeave,
      enterDiff: currentEnter - previousEnter,
      leaveDiff: currentLeave - previousLeave,
      enterDiffClass: diffClass(currentEnter - previousEnter),
      leaveDiffClass: diffClass(currentLeave - previousLeave),
      currentDateLabel: tooltipLabelByTimestamp(row.current_timestamp),
      previousDateLabel: tooltipLabelByTimestamp(row.previous_timestamp),
    };
  });
});

const waitingChartCollection = computed(() => ({
  labels: waitingRows.value.map(row => labelByTimestamp(row.current_timestamp)),
  datasets: [
    {
      label: `${t('QUEUED_CUSTOMERS_REPORT.TIME_TO_ENTER_CHAT')} (${currentPeriodLabel.value})`,
      data: waitingRows.value.map(row => row.currentEnter),
      backgroundColor: '#60A5FA',
      borderRadius: 4,
      categoryPercentage: 0.8,
      barPercentage: 0.9,
    },
    {
      label: `${t('QUEUED_CUSTOMERS_REPORT.TIME_TO_ENTER_CHAT')} (${previousPeriodLabel.value})`,
      data: waitingRows.value.map(row => row.previousEnter),
      backgroundColor: '#E9D58A',
      borderRadius: 4,
      categoryPercentage: 0.8,
      barPercentage: 0.9,
    },
    {
      label: `${t('QUEUED_CUSTOMERS_REPORT.TIME_TO_LEAVE_QUEUE')} (${currentPeriodLabel.value})`,
      data: waitingRows.value.map(row => row.currentLeave),
      backgroundColor: '#C23881',
      borderRadius: 4,
      categoryPercentage: 0.8,
      barPercentage: 0.9,
    },
    {
      label: `${t('QUEUED_CUSTOMERS_REPORT.TIME_TO_LEAVE_QUEUE')} (${previousPeriodLabel.value})`,
      data: waitingRows.value.map(row => row.previousLeave),
      backgroundColor: '#F2C2A8',
      borderRadius: 4,
      categoryPercentage: 0.8,
      barPercentage: 0.9,
    },
  ],
}));

const waitingChartOptions = {
  plugins: {
    legend: {
      display: false,
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        callback: value => `${Math.round(Number(value) / 60)} min`,
      },
    },
  },
};

const waitingCurrentEnter = computed(() =>
  Number(waitingSummary.value?.current_time_to_enter_seconds || 0)
);
const waitingPreviousEnter = computed(() =>
  Number(waitingSummary.value?.previous_time_to_enter_seconds || 0)
);
const waitingCurrentLeave = computed(() =>
  Number(waitingSummary.value?.current_time_to_leave_seconds || 0)
);
const waitingPreviousLeave = computed(() =>
  Number(waitingSummary.value?.previous_time_to_leave_seconds || 0)
);

const dayColumns = computed(() => {
  return Array.from({ length: currentPeriod.value.days }, (_, index) => {
    const date = addDays(currentPeriod.value.fromDate, index);
    return {
      key: format(date, 'yyyy-MM-dd'),
      label: format(date, 'dd MMM'),
      tooltipLabel: format(date, 'dd MMM yy'),
    };
  });
});

const hourlyRows = computed(() =>
  Array.from({ length: 24 }, (_, hour) => hour)
);

const heatmapValueMap = computed(() =>
  heatmapSeries.value.reduce((acc, item) => {
    const date = fromUnixTime(item.timestamp);
    const dayKey = format(date, 'yyyy-MM-dd');
    const key = `${dayKey}-${date.getHours()}`;
    acc[key] = Number(item.value || 0);
    return acc;
  }, {})
);

const heatmapRows = computed(() =>
  hourlyRows.value.map(hour => ({
    hour,
    cells: dayColumns.value.map(day => {
      const key = `${day.key}-${hour}`;
      const value = heatmapValueMap.value[key] || 0;
      return {
        key,
        value,
        dayLabel: day.tooltipLabel,
      };
    }),
  }))
);

const heatmapValues = computed(() =>
  Object.values(heatmapValueMap.value).map(value => Number(value || 0))
);

const heatmapQuantiles = computed(() =>
  getQuantileIntervals(heatmapValues.value, HEATMAP_QUANTILES)
);

const getHeatmapCellClass = value => {
  if (!value) return 'bg-n-slate-2';

  let level = [...heatmapQuantiles.value, Infinity].findIndex(
    range => value <= range && value > 0
  );

  if (level > 6) level = 5;
  if (level === 0) return 'bg-n-slate-2';

  const classes = [
    'bg-red-100',
    'bg-red-200',
    'bg-red-300',
    'bg-red-400',
    'bg-red-500',
    'bg-red-600',
  ];
  return classes[level - 1];
};

const formatHourLabel = hour => {
  const hour12 = hour % 12 || 12;
  const suffix = hour < 12 ? 'am' : 'pm';
  return `${hour12}:00 ${suffix}`;
};

const onHeatmapCellEnter = (event, cell, hour) => {
  const wrapEl = heatmapWrapRef.value;
  const target = event?.currentTarget;
  if (!wrapEl || !target) return;
  const wrapRect = wrapEl.getBoundingClientRect();
  const rect = target.getBoundingClientRect();
  hoveredHeatmapCell.value = {
    left: rect.left - wrapRect.left + rect.width / 2,
    top: rect.top - wrapRect.top,
    hour,
    dayLabel: cell.dayLabel,
    value: Number(cell.value || 0),
  };
};

const onHeatmapCellLeave = () => {
  hoveredHeatmapCell.value = null;
};

const generatedAtLabel = computed(() => {
  if (!generatedAt.value) return t('LAST_7_DAYS_REPORT.NOT_GENERATED_YET');
  return generatedAt.value.toLocaleString();
});

const toCsvValue = value => {
  const stringValue = String(value ?? '');
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const rowsToCsv = (headers, rows) => {
  const csvRows = [headers, ...rows];
  return csvRows
    .map(row => row.map(column => toCsvValue(column)).join(','))
    .join('\n');
};

const downloadConversionBreakdownCsv = () => {
  const headers = [
    t('QUEUED_CUSTOMERS_REPORT.SERIES'),
    ...conversionRows.value.map(
      row =>
        `${row.currentDateLabel} ${t('QUEUED_CUSTOMERS_REPORT.VS')} ${row.previousDateLabel}`
    ),
  ];

  const rows = [
    [
      t('QUEUED_CUSTOMERS_REPORT.QUEUED_CUSTOMERS'),
      ...conversionRows.value.map(
        row =>
          `${row.currentQueued} | ${row.previousQueued} | ${formatDiff(row.queuedDiff)}`
      ),
    ],
    [
      t('QUEUED_CUSTOMERS_REPORT.ENTERED_CHAT'),
      ...conversionRows.value.map(
        row =>
          `${row.currentEntered} | ${row.previousEntered} | ${formatDiff(row.enteredDiff)}`
      ),
    ],
    [
      t('QUEUED_CUSTOMERS_REPORT.LEFT_QUEUE'),
      ...conversionRows.value.map(
        row =>
          `${row.currentLeft} | ${row.previousLeft} | ${formatDiff(row.leftDiff)}`
      ),
    ],
  ];

  const csvContent = rowsToCsv(headers, rows);
  const fileName = generateFileName({
    type: 'queued-customers-breakdown',
    to: currentPeriod.value.to,
  });
  downloadCsvFile(fileName, csvContent);
};

const downloadWaitingBreakdownCsv = () => {
  const headers = [
    t('QUEUED_CUSTOMERS_REPORT.SERIES'),
    ...waitingRows.value.map(
      row =>
        `${row.currentDateLabel} ${t('QUEUED_CUSTOMERS_REPORT.VS')} ${row.previousDateLabel}`
    ),
  ];

  const rows = [
    [
      t('QUEUED_CUSTOMERS_REPORT.TIME_TO_ENTER_CHAT'),
      ...waitingRows.value.map(
        row =>
          `${formatDuration(row.currentEnter)} | ${formatDuration(
            row.previousEnter
          )} | ${formatDurationDiff(row.enterDiff)}`
      ),
    ],
    [
      t('QUEUED_CUSTOMERS_REPORT.TIME_TO_LEAVE_QUEUE'),
      ...waitingRows.value.map(
        row =>
          `${formatDuration(row.currentLeave)} | ${formatDuration(
            row.previousLeave
          )} | ${formatDurationDiff(row.leaveDiff)}`
      ),
    ],
  ];

  const csvContent = rowsToCsv(headers, rows);
  const fileName = generateFileName({
    type: 'queued-customers-average-waiting-time',
    to: currentPeriod.value.to,
  });
  downloadCsvFile(fileName, csvContent);
};

const downloadHeatmapCsv = () => {
  const headers = [
    t('QUEUED_CUSTOMERS_REPORT.HOUR'),
    ...dayColumns.value.map(day => day.label),
  ];
  const rows = heatmapRows.value.map(row => [
    formatHourLabel(row.hour),
    ...row.cells.map(cell => cell.value),
  ]);

  const csvContent = rowsToCsv(headers, rows);
  const fileName = generateFileName({
    type: 'queued-customers-heatmap',
    to: currentPeriod.value.to,
  });
  downloadCsvFile(fileName, csvContent);
};

const fetchComparisonData = async () => {
  isLoading.value = true;
  errorMessage.value = '';

  try {
    const [
      queuedCurrent,
      queuedPrevious,
      leftCurrent,
      leftPrevious,
      summaryResponse,
      waitingResponse,
      heatmapResponse,
    ] = await Promise.all([
      reportsAPI.getReports({
        metric: QUEUED_METRIC_KEY,
        from: currentPeriod.value.from,
        to: currentPeriod.value.to,
        groupBy: reportGroupBy.value,
        businessHours: false,
      }),
      reportsAPI.getReports({
        metric: QUEUED_METRIC_KEY,
        from: previousPeriod.value.from,
        to: previousPeriod.value.to,
        groupBy: reportGroupBy.value,
        businessHours: false,
      }),
      reportsAPI.getReports({
        metric: LEFT_METRIC_KEY,
        from: currentPeriod.value.from,
        to: currentPeriod.value.to,
        groupBy: reportGroupBy.value,
        businessHours: false,
      }),
      reportsAPI.getReports({
        metric: LEFT_METRIC_KEY,
        from: previousPeriod.value.from,
        to: previousPeriod.value.to,
        groupBy: reportGroupBy.value,
        businessHours: false,
      }),
      reportsAPI.getSummary(
        currentPeriod.value.from,
        currentPeriod.value.to,
        'account',
        null,
        'day',
        false,
        false
      ),
      reportsAPI.getQueuedCustomersWaitingTime({
        from: currentPeriod.value.from,
        to: currentPeriod.value.to,
        groupBy: reportGroupBy.value,
      }),
      reportsAPI.getReports({
        metric: QUEUED_METRIC_KEY,
        from: currentPeriod.value.from,
        to: currentPeriod.value.to,
        groupBy: 'hour',
        businessHours: false,
      }),
    ]);

    queuedCurrentSeries.value = normalizeSeries(queuedCurrent.data);
    queuedPreviousSeries.value = normalizeSeries(queuedPrevious.data);
    leftCurrentSeries.value = normalizeSeries(leftCurrent.data);
    leftPreviousSeries.value = normalizeSeries(leftPrevious.data);
    summaryPayload.value = summaryResponse.data || {};
    waitingSummary.value = waitingResponse.data?.summary || {};
    waitingSeries.value = waitingResponse.data?.series || [];
    heatmapSeries.value = normalizeSeries(heatmapResponse.data);
    generatedAt.value = new Date();
  } catch {
    errorMessage.value = t('QUEUED_CUSTOMERS_REPORT.FETCH_ERROR');
    queuedCurrentSeries.value = [];
    queuedPreviousSeries.value = [];
    leftCurrentSeries.value = [];
    leftPreviousSeries.value = [];
    summaryPayload.value = {};
    waitingSummary.value = {};
    waitingSeries.value = [];
    heatmapSeries.value = [];
  } finally {
    isLoading.value = false;
  }
};

const handleDateRangeAction = ({ value }) => {
  toggleDateRangeMenu(false);
  selectedDateRangeId.value = value;
};

watch(selectedDateRangeId, async () => {
  await fetchComparisonData();
});

watch(isHourlyDistributionEnabled, async () => {
  await fetchComparisonData();
});

onMounted(async () => {
  await fetchComparisonData();
});
</script>

<template>
  <div class="qc-page">
    <div class="qc-shell">
      <div class="qc-header">
        <div class="qc-header-left">
          <h1 class="qc-title">{{ t('QUEUED_CUSTOMERS_REPORT.TITLE') }}</h1>
        </div>
      </div>

      <div class="qc-toolbar">
        <div class="qc-toolbar-left">
          <div
            v-on-click-outside="() => toggleDateRangeMenu(false)"
            class="qc-filter-anchor"
          >
            <button
              class="qc-secondary-btn"
              type="button"
              @click="toggleDateRangeMenu()"
            >
              <span>{{ selectedDateRangeLabel }}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="16"
                height="16"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m6 9 6 6 6-6"
                />
              </svg>
            </button>

            <div v-if="showDateRangeMenu" class="qc-menu">
              <button
                v-for="item in dateRangeMenuItems"
                :key="item.value"
                class="qc-menu-item"
                :class="{ 'is-selected': item.isSelected }"
                type="button"
                @click="
                  handleDateRangeAction({
                    action: item.action,
                    value: item.value,
                  })
                "
              >
                {{ item.label }}
              </button>
            </div>
          </div>
        </div>

        <div class="qc-toolbar-right">
          <span class="qc-distribution-label">
            {{ t('QUEUED_CUSTOMERS_REPORT.TWENTY_FOUR_HOUR_DISTRIBUTION') }}
          </span>
          <button
            class="qc-switch"
            :class="{ 'is-on': isHourlyDistributionEnabled }"
            type="button"
            @click="isHourlyDistributionEnabled = !isHourlyDistributionEnabled"
          >
            <span />
          </button>
        </div>
      </div>

      <div class="qc-content">
        <div v-if="errorMessage" class="qc-error">
          {{ errorMessage }}
        </div>

        <section class="qc-card">
          <div class="qc-card-header">
            <div class="qc-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                />
              </svg>
              <h2 class="qc-card-title">
                {{ t('QUEUED_CUSTOMERS_REPORT.SECTION_TITLE') }}
              </h2>
              <span class="qc-period">{{ currentPeriodLabel }}</span>
            </div>
          </div>

          <div class="qc-section qc-section--no-bottom">
            <div
              class="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6"
            >
              <div>
                <p class="flex items-center gap-1 text-sm text-n-slate-12">
                  <span class="size-3 rounded-sm bg-n-blue-9" />
                  {{ t('QUEUED_CUSTOMERS_REPORT.QUEUED_CUSTOMERS') }}
                </p>
                <p class="text-xs text-n-slate-11">{{ currentPeriodLabel }}</p>
                <p class="text-3xl font-semibold text-n-slate-12">
                  {{ currentQueuedTotal.toLocaleString() }}
                </p>
              </div>
              <div>
                <p class="flex items-center gap-1 text-sm text-n-slate-12">
                  <span class="size-3 rounded-sm bg-n-amber-9" />
                  {{ t('QUEUED_CUSTOMERS_REPORT.QUEUED_CUSTOMERS') }}
                </p>
                <p class="text-xs text-n-slate-11">{{ previousPeriodLabel }}</p>
                <p class="text-3xl font-semibold text-n-slate-12">
                  {{ previousQueuedTotal.toLocaleString() }}
                </p>
              </div>
              <div>
                <p class="flex items-center gap-1 text-sm text-n-slate-12">
                  <span class="size-3 rounded-sm bg-n-magenta-9" />
                  {{ t('QUEUED_CUSTOMERS_REPORT.ENTERED_CHAT') }}
                </p>
                <p class="text-xs text-n-slate-11">{{ currentPeriodLabel }}</p>
                <p class="text-3xl font-semibold text-n-slate-12">
                  {{ currentEnteredTotal.toLocaleString() }}
                  <span class="text-base text-n-slate-11">
                    ({{ formatPercent(currentEnteredRate) }})
                  </span>
                </p>
              </div>
              <div>
                <p class="flex items-center gap-1 text-sm text-n-slate-12">
                  <span class="size-3 rounded-sm bg-orange-500" />
                  {{ t('QUEUED_CUSTOMERS_REPORT.ENTERED_CHAT') }}
                </p>
                <p class="text-xs text-n-slate-11">{{ previousPeriodLabel }}</p>
                <p class="text-3xl font-semibold text-n-slate-12">
                  {{ previousEnteredTotal.toLocaleString() }}
                  <span class="text-base text-n-slate-11">
                    ({{ formatPercent(previousEnteredRate) }})
                  </span>
                </p>
              </div>
              <div>
                <p class="flex items-center gap-1 text-sm text-n-slate-12">
                  <span class="size-3 rounded-sm bg-n-slate-8" />
                  {{ t('QUEUED_CUSTOMERS_REPORT.LEFT_QUEUE') }}
                </p>
                <p class="text-xs text-n-slate-11">{{ currentPeriodLabel }}</p>
                <p class="text-3xl font-semibold text-n-slate-12">
                  {{ currentLeftTotal.toLocaleString() }}
                  <span class="text-base text-n-slate-11">
                    ({{ formatPercent(currentLeftRate) }})
                  </span>
                </p>
              </div>
              <div>
                <p class="flex items-center gap-1 text-sm text-n-slate-12">
                  <span class="size-3 rounded-sm bg-violet-600" />
                  {{ t('QUEUED_CUSTOMERS_REPORT.LEFT_QUEUE') }}
                </p>
                <p class="text-xs text-n-slate-11">{{ previousPeriodLabel }}</p>
                <p class="text-3xl font-semibold text-n-slate-12">
                  {{ previousLeftTotal.toLocaleString() }}
                  <span class="text-base text-n-slate-11">
                    ({{ formatPercent(previousLeftRate) }})
                  </span>
                </p>
              </div>
            </div>
          </div>

          <div class="qc-chart">
            <woot-loading-state
              v-if="isLoading"
              :message="$t('REPORT.LOADING_CHART')"
              class="h-full"
            />
            <div
              v-else-if="!conversionRows.length"
              class="flex h-full items-center justify-center text-sm text-n-slate-11"
            >
              {{ t('QUEUED_CUSTOMERS_REPORT.NO_DATA') }}
            </div>
            <BarChart
              v-else
              :collection="conversionChartCollection"
              :chart-options="defaultChartOptions"
            />
          </div>

          <div class="qc-breakdown">
            <div
              class="qc-breakdown-header"
              role="button"
              tabindex="0"
              @click="isConversionBreakdownOpen = !isConversionBreakdownOpen"
              @keydown.enter.prevent="
                isConversionBreakdownOpen = !isConversionBreakdownOpen
              "
              @keydown.space.prevent="
                isConversionBreakdownOpen = !isConversionBreakdownOpen
              "
            >
              <div class="qc-breakdown-title">
                <svg
                  class="qc-breakdown-caret"
                  :class="{ 'is-collapsed': !isConversionBreakdownOpen }"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m6 9 6 6 6-6"
                  />
                </svg>
                <span>{{ t('QUEUED_CUSTOMERS_REPORT.BREAKDOWN_TITLE') }}</span>
              </div>

              <button
                class="qc-export-btn"
                type="button"
                @click.stop="downloadConversionBreakdownCsv"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                  />
                </svg>
                <span>{{ t('QUEUED_CUSTOMERS_REPORT.EXPORT_CSV') }}</span>
              </button>
            </div>

            <div v-show="isConversionBreakdownOpen" class="qc-table-wrap">
              <table class="min-w-full text-sm">
                <thead>
                  <tr class="text-left text-n-slate-11">
                    <th class="whitespace-nowrap px-3 py-2">
                      {{ t('QUEUED_CUSTOMERS_REPORT.SERIES') }}
                    </th>
                    <th
                      v-for="row in conversionRows"
                      :key="`queued-customers-header-${row.currentTimestamp}`"
                      class="whitespace-nowrap px-3 py-2"
                    >
                      <div>{{ row.currentDateLabel }}</div>
                      <div class="text-xs text-n-slate-10">
                        {{ t('QUEUED_CUSTOMERS_REPORT.VS') }}
                        {{ row.previousDateLabel }}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="border-t border-n-weak">
                    <td
                      class="whitespace-nowrap px-3 py-3 font-medium text-n-slate-12"
                    >
                      {{ t('QUEUED_CUSTOMERS_REPORT.QUEUED_CUSTOMERS') }}
                    </td>
                    <td
                      v-for="row in conversionRows"
                      :key="`queued-customers-row-queued-${row.currentTimestamp}`"
                      class="whitespace-nowrap px-3 py-3 align-top"
                    >
                      <div class="font-semibold text-n-slate-12">
                        {{ row.currentQueued }}
                      </div>
                      <div class="text-xs text-n-slate-11">
                        {{ row.previousQueued }}
                      </div>
                      <div
                        class="text-xs font-medium"
                        :class="row.queuedDiffClass"
                      >
                        {{ formatDiff(row.queuedDiff) }}
                      </div>
                    </td>
                  </tr>
                  <tr class="border-t border-n-weak">
                    <td
                      class="whitespace-nowrap px-3 py-3 font-medium text-n-slate-12"
                    >
                      {{ t('QUEUED_CUSTOMERS_REPORT.ENTERED_CHAT') }}
                    </td>
                    <td
                      v-for="row in conversionRows"
                      :key="`queued-customers-row-entered-${row.currentTimestamp}`"
                      class="whitespace-nowrap px-3 py-3 align-top"
                    >
                      <div class="font-semibold text-n-slate-12">
                        {{ row.currentEntered }}
                      </div>
                      <div class="text-xs text-n-slate-11">
                        {{ row.previousEntered }}
                      </div>
                      <div
                        class="text-xs font-medium"
                        :class="row.enteredDiffClass"
                      >
                        {{ formatDiff(row.enteredDiff) }}
                      </div>
                    </td>
                  </tr>
                  <tr class="border-t border-n-weak">
                    <td
                      class="whitespace-nowrap px-3 py-3 font-medium text-n-slate-12"
                    >
                      {{ t('QUEUED_CUSTOMERS_REPORT.LEFT_QUEUE') }}
                    </td>
                    <td
                      v-for="row in conversionRows"
                      :key="`queued-customers-row-left-${row.currentTimestamp}`"
                      class="whitespace-nowrap px-3 py-3 align-top"
                    >
                      <div class="font-semibold text-n-slate-12">
                        {{ row.currentLeft }}
                      </div>
                      <div class="text-xs text-n-slate-11">
                        {{ row.previousLeft }}
                      </div>
                      <div
                        class="text-xs font-medium"
                        :class="row.leftDiffClass"
                      >
                        {{ formatDiff(row.leftDiff) }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section class="qc-card">
          <div class="border-b border-n-weak px-4 py-4">
            <h2 class="text-xl font-semibold text-n-slate-12">
              {{ t('QUEUED_CUSTOMERS_REPORT.AVERAGE_WAITING_TITLE') }}
            </h2>

            <div class="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <p class="flex items-center gap-1 text-sm text-n-slate-12">
                  <span class="size-3 rounded-sm bg-sky-400" />
                  {{ t('QUEUED_CUSTOMERS_REPORT.TIME_TO_ENTER_CHAT') }}
                </p>
                <p class="text-xs text-n-slate-11">{{ currentPeriodLabel }}</p>
                <p class="text-3xl font-semibold text-n-slate-12">
                  {{ formatDuration(waitingCurrentEnter) }}
                </p>
              </div>
              <div>
                <p class="flex items-center gap-1 text-sm text-n-slate-12">
                  <span class="size-3 rounded-sm bg-n-amber-7" />
                  {{ t('QUEUED_CUSTOMERS_REPORT.TIME_TO_ENTER_CHAT') }}
                </p>
                <p class="text-xs text-n-slate-11">{{ previousPeriodLabel }}</p>
                <p class="text-3xl font-semibold text-n-slate-12">
                  {{ formatDuration(waitingPreviousEnter) }}
                </p>
              </div>
              <div>
                <p class="flex items-center gap-1 text-sm text-n-slate-12">
                  <span class="size-3 rounded-sm bg-n-magenta-9" />
                  {{ t('QUEUED_CUSTOMERS_REPORT.TIME_TO_LEAVE_QUEUE') }}
                </p>
                <p class="text-xs text-n-slate-11">{{ currentPeriodLabel }}</p>
                <p class="text-3xl font-semibold text-n-slate-12">
                  {{ formatDuration(waitingCurrentLeave) }}
                </p>
              </div>
              <div>
                <p class="flex items-center gap-1 text-sm text-n-slate-12">
                  <span class="size-3 rounded-sm bg-orange-200" />
                  {{ t('QUEUED_CUSTOMERS_REPORT.TIME_TO_LEAVE_QUEUE') }}
                </p>
                <p class="text-xs text-n-slate-11">{{ previousPeriodLabel }}</p>
                <p class="text-3xl font-semibold text-n-slate-12">
                  {{ formatDuration(waitingPreviousLeave) }}
                </p>
              </div>
            </div>
          </div>

          <div class="h-96 px-4 py-4">
            <woot-loading-state
              v-if="isLoading"
              :message="$t('REPORT.LOADING_CHART')"
              class="h-full"
            />
            <div
              v-else-if="!waitingRows.length"
              class="flex h-full items-center justify-center text-sm text-n-slate-11"
            >
              {{ t('QUEUED_CUSTOMERS_REPORT.NO_DATA') }}
            </div>
            <BarChart
              v-else
              :collection="waitingChartCollection"
              :chart-options="waitingChartOptions"
            />
          </div>

          <div class="border-t border-n-weak px-4 py-4">
            <div class="mb-3 flex items-center justify-between gap-2">
              <h3 class="text-xl font-semibold text-n-slate-12">
                {{
                  t('QUEUED_CUSTOMERS_REPORT.AVERAGE_WAITING_BREAKDOWN_TITLE')
                }}
              </h3>
              <button
                class="qc-export-btn"
                type="button"
                @click="downloadWaitingBreakdownCsv"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                  />
                </svg>
                <span>{{ t('QUEUED_CUSTOMERS_REPORT.EXPORT_CSV') }}</span>
              </button>
            </div>

            <div class="overflow-x-auto">
              <table class="min-w-full text-sm">
                <thead>
                  <tr class="text-left text-n-slate-11">
                    <th class="whitespace-nowrap px-3 py-2">
                      {{ t('QUEUED_CUSTOMERS_REPORT.SERIES') }}
                    </th>
                    <th
                      v-for="row in waitingRows"
                      :key="`waiting-header-${row.current_timestamp}`"
                      class="whitespace-nowrap px-3 py-2"
                    >
                      <div>{{ row.currentDateLabel }}</div>
                      <div class="text-xs text-n-slate-10">
                        {{ t('QUEUED_CUSTOMERS_REPORT.VS') }}
                        {{ row.previousDateLabel }}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="border-t border-n-weak">
                    <td
                      class="whitespace-nowrap px-3 py-3 font-medium text-n-slate-12"
                    >
                      {{ t('QUEUED_CUSTOMERS_REPORT.TIME_TO_ENTER_CHAT') }}
                    </td>
                    <td
                      v-for="row in waitingRows"
                      :key="`waiting-enter-${row.current_timestamp}`"
                      class="whitespace-nowrap px-3 py-3 align-top"
                    >
                      <div class="font-semibold text-n-slate-12">
                        {{ formatDuration(row.currentEnter) }}
                      </div>
                      <div class="text-xs text-n-slate-11">
                        {{ formatDuration(row.previousEnter) }}
                      </div>
                      <div
                        class="text-xs font-medium"
                        :class="row.enterDiffClass"
                      >
                        {{ formatDurationDiff(row.enterDiff) }}
                      </div>
                    </td>
                  </tr>
                  <tr class="border-t border-n-weak">
                    <td
                      class="whitespace-nowrap px-3 py-3 font-medium text-n-slate-12"
                    >
                      {{ t('QUEUED_CUSTOMERS_REPORT.TIME_TO_LEAVE_QUEUE') }}
                    </td>
                    <td
                      v-for="row in waitingRows"
                      :key="`waiting-leave-${row.current_timestamp}`"
                      class="whitespace-nowrap px-3 py-3 align-top"
                    >
                      <div class="font-semibold text-n-slate-12">
                        {{ formatDuration(row.currentLeave) }}
                      </div>
                      <div class="text-xs text-n-slate-11">
                        {{ formatDuration(row.previousLeave) }}
                      </div>
                      <div
                        class="text-xs font-medium"
                        :class="row.leaveDiffClass"
                      >
                        {{ formatDurationDiff(row.leaveDiff) }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section class="qc-card">
          <div class="qc-section">
            <div class="mb-4 flex items-center justify-between gap-2">
              <div>
                <h2 class="text-xl font-semibold text-n-slate-12">
                  {{ t('QUEUED_CUSTOMERS_REPORT.HEATMAP_TITLE') }}
                </h2>
                <p class="mt-1 text-sm text-n-ruby-10">
                  {{ t('QUEUED_CUSTOMERS_REPORT.HEATMAP_LIMITATION') }}
                </p>
              </div>
              <button
                class="qc-export-btn"
                type="button"
                @click="downloadHeatmapCsv"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                  />
                </svg>
                <span>{{ t('QUEUED_CUSTOMERS_REPORT.EXPORT_CSV') }}</span>
              </button>
            </div>

            <div ref="heatmapWrapRef" class="qc-heatmap-wrap">
              <woot-loading-state
                v-if="isLoading"
                :message="$t('REPORT.LOADING_CHART')"
              />

              <div v-else-if="heatmapRows.length" class="qc-heatmap-table-wrap">
                <div
                  v-if="hoveredHeatmapCell"
                  class="qc-heatmap-tooltip"
                  :style="{
                    left: `${hoveredHeatmapCell.left}px`,
                    top: `${hoveredHeatmapCell.top}px`,
                  }"
                >
                  <div class="qc-heatmap-tooltip__time">
                    {{ hoveredHeatmapCell.dayLabel }}
                    {{ formatHourLabel(hoveredHeatmapCell.hour) }}
                  </div>
                  <div class="qc-heatmap-tooltip__line">
                    <span class="qc-heatmap-tooltip__dot" />
                    <span>
                      {{ t('QUEUED_CUSTOMERS_REPORT.HEATMAP_TITLE') }}:
                      {{ hoveredHeatmapCell.value.toLocaleString() }}
                    </span>
                  </div>
                </div>

                <table class="qc-heatmap-table">
                  <thead>
                    <tr>
                      <th class="qc-heatmap-th qc-heatmap-th--hour">
                        {{ t('QUEUED_CUSTOMERS_REPORT.HOUR') }}
                      </th>
                      <th
                        v-for="day in dayColumns"
                        :key="day.key"
                        class="qc-heatmap-th"
                      >
                        {{ day.label }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in heatmapRows" :key="`hour-${row.hour}`">
                      <td class="qc-heatmap-td qc-heatmap-td--hour">
                        {{
                          row.hour % 3 === 0 ? formatHourLabel(row.hour) : ''
                        }}
                      </td>
                      <td
                        v-for="cell in row.cells"
                        :key="cell.key"
                        class="qc-heatmap-td"
                        @mouseenter="onHeatmapCellEnter($event, cell, row.hour)"
                        @mouseleave="onHeatmapCellLeave"
                      >
                        <div
                          class="qc-heatmap-cell"
                          :class="getHeatmapCellClass(cell.value)"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div v-else class="qc-empty qc-empty--heatmap">
                {{ t('QUEUED_CUSTOMERS_REPORT.NO_DATA') }}
              </div>
            </div>

            <div
              class="mt-4 flex items-center justify-end gap-4 text-xs text-n-slate-11"
            >
              <span>1</span>
              <span class="h-2 w-8 rounded-sm bg-red-100" />
              <span class="h-2 w-8 rounded-sm bg-red-200" />
              <span class="h-2 w-8 rounded-sm bg-red-300" />
              <span class="h-2 w-8 rounded-sm bg-red-400" />
              <span class="h-2 w-8 rounded-sm bg-red-500" />
              <span class="h-2 w-8 rounded-sm bg-red-600" />
            </div>
          </div>
        </section>

        <p class="qc-footer-note">
          {{
            t('QUEUED_CUSTOMERS_REPORT.GENERATED_AT', {
              time: generatedAtLabel,
            })
          }}
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.qc-page {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  color: #111827;
}
.qc-shell {
  min-width: 650px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  overflow: visible;
}

.qc-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
  position: sticky;
  top: 0;
  z-index: 50;
  background: #ffffff;
}
.qc-header-left {
  display: flex;
  align-items: center;
  gap: 0;
}
.qc-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}
.qc-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 24px 8px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.3) 0px 3px 10px -6px;
  position: sticky;
  top: 58px;
  z-index: 49;
}
.qc-toolbar-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.qc-toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.qc-distribution-label {
  color: #111827;
  font-size: 14px;
  white-space: nowrap;
}

.qc-switch {
  width: 27px;
  height: 20px;
  border: none;
  border-radius: 15px;
  box-sizing: border-box;
  cursor: pointer;
  background: rgb(221, 221, 221);
  padding: 0;
  display: block;
  position: relative;
}
.qc-switch span {
  position: absolute;
  display: block;
  top: 2px;
  left: 2px;
  border-radius: 50%;
  background-color: rgb(255, 255, 255);
  box-shadow:
    rgba(0, 0, 0, 0.05) 0 3px 3px 0,
    rgba(0, 0, 0, 0.1) 0 2px 2px 0,
    rgba(0, 0, 0, 0.05) 0 3px 2px 0;
  transition:
    left 0.2s ease-in-out,
    background-color 0.2s ease-in-out;
  will-change: left, background-color;
  width: 15px;
  height: 15px;
}
.qc-switch.is-on {
  background: #2f80ed;
}
.qc-switch.is-on span {
  left: 10px;
}

.qc-filter-anchor {
  position: relative;
}
.qc-secondary-btn {
  height: 34px;
  border-radius: 8px;
  border: 1px solid #d9dde3;
  background: #ffffff;
  color: #111827;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  cursor: pointer;
}
.qc-secondary-btn:hover {
  background: #f9fafb;
}
.qc-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 220px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.12) 0px 14px 38px;
  padding: 6px;
  z-index: 30;
}
.qc-menu-item {
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  border-radius: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.qc-menu-item:hover {
  background: #f3f4f6;
}
.qc-menu-item.is-selected {
  background: #eef2ff;
  color: #1d4ed8;
}

.qc-content {
  padding: 16px 32px;
}
.qc-error {
  margin-bottom: 12px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}

.qc-card {
  border-radius: 4px;
  border: 1px solid #d8dee5;
  background: #fff;
  box-sizing: border-box;
  margin-bottom: 20px;
  overflow: hidden;
}
.qc-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #e5e7eb;
}
.qc-card-title-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.qc-card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}
.qc-period {
  font-size: 12px;
  color: #6b7280;
  margin-left: 10px;
  white-space: nowrap;
}

.qc-section {
  padding: 16px 20px 20px;
}
.qc-section--no-bottom {
  padding-bottom: 0;
}
.qc-chart {
  height: 384px;
  margin-top: 16px;
}
.qc-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 13px;
}
.qc-empty--heatmap {
  height: 160px;
}

.qc-breakdown {
  border-top: 1px solid #dfe3e8;
}
.qc-breakdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 16px;
  cursor: pointer;
  user-select: none;
}
.qc-breakdown-title {
  color: #111827;
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.qc-breakdown-caret {
  transition: transform 0.18s ease;
}
.qc-breakdown-caret.is-collapsed {
  transform: rotate(-90deg);
}

.qc-export-btn {
  min-width: 128px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.qc-export-btn:hover {
  background: #f9fafb;
}

.qc-table-wrap {
  overflow-x: auto;
  border-top: 1px solid #dfe3e8;
}

.qc-heatmap-wrap {
  overflow-x: auto;
  position: relative;
}
.qc-heatmap-table-wrap {
  position: relative;
  overflow-x: auto;
  padding-bottom: 8px;
}
.qc-heatmap-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 4px;
}
.qc-heatmap-th {
  padding: 4px 6px;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  color: #111827;
}
.qc-heatmap-th--hour {
  text-align: left;
  color: #6b7280;
}
.qc-heatmap-td {
  padding: 2px;
}
.qc-heatmap-td--hour {
  font-size: 12px;
  color: #6b7280;
  padding-right: 10px;
  white-space: nowrap;
}
.qc-heatmap-cell {
  height: 13px;
  width: 100%;
  border-radius: 2px;
}

.qc-heatmap-tooltip {
  position: absolute;
  transform: translate(-50%, calc(-100% - 10px));
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 10px 12px;
  box-shadow: rgba(0, 0, 0, 0.18) 0px 14px 38px;
  pointer-events: none;
  min-width: 220px;
  z-index: 5;
}
.qc-heatmap-tooltip__time {
  font-size: 12px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 6px;
}
.qc-heatmap-tooltip__line {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #374151;
  font-weight: 600;
}
.qc-heatmap-tooltip__dot {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background: #cbd5e1;
  display: inline-block;
}

.qc-footer-note {
  margin-top: 8px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}

.qc-content :deep(.text-n-slate-12) {
  color: #000000 !important;
}

::global(body.dark) .qc-page,
::global(body.dark) .qc-shell,
::global(body.dark) .qc-card,
::global(body.dark) .qc-header,
::global(body.dark) .qc-toolbar,
::global(body.dark) .qc-heatmap-th,
::global(body.dark) .qc-heatmap-td,
::global(body.theme-mix) .qc-page,
::global(body.theme-mix) .qc-shell,
::global(body.theme-mix) .qc-card,
::global(body.theme-mix) .qc-header,
::global(body.theme-mix) .qc-toolbar,
::global(body.theme-mix) .qc-heatmap-th,
::global(body.theme-mix) .qc-heatmap-td {
  background: #fff !important;
  color: #111827 !important;
}

::global(body.dark) .qc-heatmap-th *,
::global(body.theme-mix) .qc-heatmap-th * {
  color: #111827 !important;
}
</style>
